/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { CRUDContext } from "./ClientsTable";
import { getData } from "../../Services/getData";
import { AxiosResponse } from "axios";
import { endpoints } from "../../Utils/UrlEnum";
import SpecialDivider from "../SpecialDivider";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { updateData } from "../../Services/updateData";
import { postData } from "../../Services/postData";
import Periodicity from "./Periodicity";
import { Add, Delete, Search } from "@mui/icons-material";
import { ClientFormProps } from "../../Utils/Types";
import { FormsIds } from "../../Utils/Constants";
import Config from "../../Utils/Config";
import styles from "../../Styles/clientPanel.module.css";
import { WebSocketContext } from "../../Context/WebSocketContext";
import { ClientValidationSchema } from "../../Utils/Models";
import useCustomMemo from "../../Hooks/useCustomMemo";
import { onFormError } from "../../Utils/Utils";
import Location from "./Location";
import Address from "./Address";
import { toast } from "react-toastify";

export default function ClientForm(props: ClientFormProps) {
  const clientContext: any = useContext(CRUDContext);
  const ref: any = useRef();
  const firstUpdate = useRef(true);
  const categoryRef = useRef();
  const websocketContext = useContext(WebSocketContext);
  const [disableSearchButton, setDisabledSearchButton] = useState(false);
  const [tags, setTags] = useState([]);
  const [address, setAddress] = useState({
    country: undefined,
    county: undefined,
    city: undefined,
  });

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      phone: [""],
      periodicity: [
        {
          name: "",
          nextContactDate: undefined,
          nextTransportDate: undefined,
          noDays: Config.standardNewContactNoDays,
          day: {
            name: "",
            value: 0,
          },
        },
      ],
      isLegalPerson: false,
      receiveEmails: true,
    },
    resolver: yupResolver(ClientValidationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    context: undefined,
    shouldFocusError: false,
    shouldUnregister: false,
    criteriaMode: "firstError",
  });

  const periodicityLength = getValues("periodicity")?.length;
  const memo = useCustomMemo();
  const clientCategories = memo?.storage.clientCategories || [];

  /**
   *
   */
  useEffect(() => {
    if (clientContext?._id && clientContext?._id !== "") reset(clientContext);
  }, []);

  useEffect(() => {
    try {
      getData(endpoints.predefined)
        .then((res) => {
          setTags(res.data);
        })
        .catch((err) => {
          toast.error(err);
        });
    } catch (error) {
      toast.error(`${error}`);
    }
  }, []);

  /**
   *
   */
  useEffect(() => {
    if (clientContext?._id && clientContext?._id !== "") reset(clientContext);
    reset(props.selectedClient);

    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (!firstUpdate.current) ref.current.submit();
  }, []);

  const addPhoneNumber = () => {
    //get the current value of the phone array
    const phone = getValues("phone") || [];
    //push a new empty string to the array
    phone.push("");
    //update the value of the phone array
    setValue("phone", phone);
  };

  /**
   *
   * @param index
   */
  const deletePhoneNumber = (index: number) => {
    //get the current value of the phone array
    const phone = getValues("phone") || [];
    //remove the element from the array
    phone.splice(index, 1);
    //update the value of the phone array
    setValue("phone", phone);
  };

  /**
   *
   * @param id
   */
  const updateElement = (model: any) => {
    const url = `${endpoints.client.main}/${model?._id}`;
    updateData(url, model).then((res: AxiosResponse) => {
      if (res && res.data) {
        websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
        if (props.onSuccess) props.onSuccess(null, res.data);
        props.onClose();
      }
    });
  };

  /**
   *
   * @param id
   */
  const createElement = (model: any) => {
    const url = `${endpoints.client.main}`;
    postData(url, model).then((res: AxiosResponse) => {
      if (!res || res?.data.errors) {
        return;
      }
      websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
      if (props.onSuccess) props.onSuccess(null, res.data);
      props.onClose();
    });
  };

  /**
   *
   * @param model
   */
  const onSubmit = (model: any) => {
    model._id ? updateElement(model) : createElement(model);
  };

  function formatString(input: string): string {
    return input
      .replace(/\b(MUNICIPIUL|Mun\.)\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();
  }

  /**
   *
   */
  const autocompleteWithCompanyData = async () => {
    const fiscalCode: any = getValues("fiscalCode");
    if (!fiscalCode) return;
    const date = new Date();
    await postData(`${endpoints.client.main}/cui`, [
      {
        cui: +fiscalCode,
        data: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDay()}`,
      },
    ])
      .then(async (res) => {
        setDisabledSearchButton(true);
        const data = res.data;

        const countriesAndCounties = await (
          await getData(endpoints.client.addressCheckEndpointState)
        ).data.data;

        const countryObject = countriesAndCounties.find(
          (c: any) => c.name.toLowerCase() === "romania"
        );

        if (countryObject) {
          let formatCounty = formatString(data.judet);
          if (formatCounty === "BUCUREŞTI") {
            formatCounty = "Bucharest";
          }
          const countyObject = countryObject.states.find((c: any) => {
            const normalize = (str: string) =>
              str
                ?.toLowerCase()
                .replace(/\s*county\s*$/i, "") // remove "county" at end
                .trim();
            return normalize(c.name) === normalize(formatCounty);
          });
          if (countyObject) {
            const cities = await postData(
              endpoints.client.addressCheckEndpointStateCity,
              {
                country: countryObject.name,
                state: countyObject.name,
              }
            );
            const city = cities.data.data.find((c: any) =>
              data.localitate.toLowerCase().includes(c.toLowerCase())
            );
            setAddress({
              country: countryObject,
              county: countyObject,
              city: city,
            });
          }
        }

        setValue("companyName", data.denumire);
        setValue("tradeRegisterNo", data.nrRegCom);

        setValue("address.country", "Romania");
        setValue("address.county", data.judet);
        setValue("address.city", data.localitate);

        setValue("address.details", data.detalii);
        setValue("address.postcode", data.codPostal);
      })
      .catch((err) => {
        toast.error(err.statusCode);
      });
  };

  /**
   * Wrapper for the component to pass other props
   * @returns
   */
  const AddNewOptionButtonWrapper: React.FC<any> = ({
    children,
    passFlag,
    ...other
  }) => (
    <Paper {...other}>
      <IconButton
        onMouseDown={(event) => {
          event.preventDefault();
          if (props?.handleContentChange) props?.handleContentChange(passFlag);
        }}
        className={styles.dropDownButton}
        size="small"
        aria-label="editDropdown"
      >
        <Add fontSize="inherit" />
      </IconButton>
      {children}
    </Paper>
  );

  /**
   * Add a new working point to the client form.
   * @return {void}
   */
  const addWorkingPoint = () => {
    //get the current value of the locations array
    const locationType = getValues("locations") || [];
    //push a new empty object to the array
    locationType.push({
      address: {
        city: "",
        county: "",
        country: "",
        details: "",
        postcode: "",
      },
      bank: "",
      iban: "",
      observations: "",
      contactPerson: {
        name: "",
        phone: "",
        email: "",
      },
      notes: [],
      isWorkingPoint: false,
    });
    //update the value of the locations array
    setValue("locations", locationType);
  };

  const deleteWorkingPoint = (index: number) => {
    //get the current value of the locations array
    const locationType = getValues("locations") || [];
    if (locationType.length === 1) {
      return; // Do not allow deletion if there is only one working point
    }
    //remove the element from the array
    locationType.splice(index, 1);
    //update the value of the locations array
    setValue("locations", locationType);
  };
  return (
    <Fragment>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit, onFormError)(e);
        }}
        method="get"
        ref={ref}
        id={FormsIds.clients}
        className={styles.clientFormContainer}
      >
        <Grid container spacing={Config.standardGridSpacing}>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="isLegalPerson"
              control={control}
              render={({ field: props }) => {
                return (
                  <FormControl
                    style={{ width: "100%", textAlign: "start" }}
                    error={typeof formState.errors.isLegalPerson === "object"}
                    component="fieldset"
                    variant="standard"
                  >
                    <FormLabel component="legend">
                      {Vocabulary.clientType}
                    </FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        className={
                          typeof formState.errors.isLegalPerson === "object"
                            ? styles.checkboxTextError
                            : ""
                        }
                        control={
                          <Checkbox
                            className={
                              typeof formState.errors.isLegalPerson === "object"
                                ? styles.checkboxTextError
                                : ""
                            }
                            checked={watch(`isLegalPerson`) === true}
                            onChange={(e) => props.onChange(e.target.checked)}
                            name="legalPerson"
                          />
                        }
                        label={Vocabulary.legalPerson}
                      />
                      <FormControlLabel
                        className={
                          typeof formState.errors.isLegalPerson === "object"
                            ? styles.checkboxTextError
                            : ""
                        }
                        control={
                          <Checkbox
                            className={
                              typeof formState.errors.isLegalPerson === "object"
                                ? styles.checkboxTextError
                                : ""
                            }
                            checked={watch(`isLegalPerson`) === false}
                            onChange={(e) => props.onChange(false)}
                            name="physicalPerson"
                          />
                        }
                        label={Vocabulary.physicalPerson}
                      />
                    </FormGroup>
                    <FormHelperText>
                      {formState.errors.isLegalPerson?.message?.toString()}
                    </FormHelperText>
                  </FormControl>
                );
              }}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            {watch("isLegalPerson") === true ? (
              <TextField
                {...register("fiscalCode")}
                name="fiscalCode"
                id="fiscalCode"
                value={watch("fiscalCode") || ""}
                label={Vocabulary.fiscalCode}
                fullWidth
                variant="outlined"
                error={typeof formState.errors.fiscalCode === "object"}
                helperText={formState.errors.fiscalCode?.message?.toString()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={disableSearchButton}
                        onClick={() => {
                          autocompleteWithCompanyData();
                        }}
                      >
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ) : watch("isLegalPerson") === false ? (
              <TextField
                {...register("CNP")}
                name="CNP"
                id="CNP"
                label={Vocabulary.CNP}
                value={watch("CNP") || ""}
                fullWidth
                type="number"
                variant="outlined"
                error={typeof formState.errors.CNP === "object"}
                helperText={formState.errors.CNP?.message?.toString()}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ) : null}
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            {watch("isLegalPerson") === true ? (
              <TextField
                {...register("companyName")}
                id="companyName"
                name="companyName"
                label={Vocabulary.companyName}
                fullWidth
                error={typeof formState.errors.companyName === "object"}
                helperText={formState.errors.companyName?.message?.toString()}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ) : watch("isLegalPerson") === false ? (
              <TextField
                {...register("name")}
                id="name"
                name="name"
                label={Vocabulary.name}
                fullWidth
                error={typeof formState.errors.name === "object"}
                helperText={formState.errors.name?.message?.toString()}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            ) : null}
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <TextField
              {...register("email")}
              variant="outlined"
              name="email"
              id="email"
              label={Vocabulary.email}
              fullWidth
              error={typeof formState.errors.email === "object"}
              helperText={formState.errors.email?.message?.toString()}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <Controller
              name="tags"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disableClearable
                  multiple
                  disablePortal
                  onChange={(event, value) => field.onChange(value)}
                  getOptionDisabled={(option: any) =>
                    !!getValues("tags")?.find(
                      (element: any) => element?._id === option?._id
                    )
                  }
                  value={getValues("tags") || []}
                  options={tags}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value
                  }
                  getOptionLabel={(option: any) => option.name}
                  id="tags"
                  renderInput={(params) => (
                    <TextField
                      error={!!error}
                      helperText={error?.message}
                      label={Vocabulary.tags}
                      name="tags"
                      type="search"
                      style={{ width: "100%" }}
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="category"
              control={control}
              defaultValue={undefined}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Autocomplete
                  freeSolo={false}
                  disableClearable
                  multiple
                  disablePortal
                  onChange={(event, value) => {
                    field.onChange(value);
                  }}
                  getOptionDisabled={(option: any) =>
                    !!getValues("category")?.find(
                      (element: any) => element?._id === option?._id
                    )
                  }
                  ref={categoryRef}
                  value={getValues("category") || []}
                  options={clientCategories}
                  PaperComponent={AddNewOptionButtonWrapper}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value
                  }
                  getOptionLabel={(option: any) => option.name}
                  id="category"
                  renderInput={(params) => (
                    <TextField
                      error={!!error}
                      helperText={error?.message}
                      label={Vocabulary.category}
                      name="category"
                      type="search"
                      style={{ width: "100%" }}
                      inputRef={ref}
                      {...params}
                    />
                  )}
                />
              )}
            />
          </Grid>

          {watch("isLegalPerson") && (
            <Grid item xs={12} sm={12} md={6}>
              <TextField
                {...register("tradeRegisterNo")}
                name="tradeRegisterNo"
                id="tradeRegisterNo"
                label={Vocabulary.tradeRegisterNo}
                fullWidth
                variant="outlined"
                error={typeof formState.errors.tradeRegisterNo === "object"}
                helperText={formState.errors.tradeRegisterNo?.message?.toString()}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          )}

          {/* ///////////////////////////////////////////////////////////////////////////NR. DE TELEFON */}
          <Grid item xs={12} sm={12} md={12}>
            <SpecialDivider
              title={Vocabulary.phoneNumbers}
              button={
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addPhoneNumber}
                >
                  {Vocabulary.addPhoneNumber}
                </Button>
              }
            />
          </Grid>

          {watch("phone")?.map((phone: any, index: number) => (
            <Grid item xs={12} sm={12} md={4} key={index}>
              <Controller
                name={`phone.${index}`}
                control={control}
                render={({ field: { onChange, value, ref } }) => (
                  <TextField
                    variant="outlined"
                    name={`phone[${index}]`}
                    id={`phone[${index}]`}
                    label={`${Vocabulary.phone} #${index + 1}`}
                    fullWidth
                    value={value || ""}
                    onChange={onChange}
                    error={typeof formState.errors.phone?.[index] === "object"}
                    helperText={formState.errors.phone?.[
                      index
                    ]?.message?.toString()}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton onClick={() => deletePhoneNumber(index)}>
                            <Delete />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    ref={ref}
                  />
                )}
              />
            </Grid>
          ))}

          {/* ///////////////////////////////////////////////////////////////////////////ADRESA */}
          <Address
            register={register}
            index={0}
            control={control}
            getValues={getValues}
            title={Vocabulary.invoiceAddress}
            isArray={false}
            country={address.country}
            county={address.county}
            city={address.city}
          />
          {/* ///////////////////////////////////////////////////////////////////////////PERIODICITATE */}
          <Grid item xs={12} sm={12} md={12}>
            <SpecialDivider title={Vocabulary.periodicity} />
          </Grid>

          <Periodicity
            periodicityLength={periodicityLength}
            control={control}
            getValues={getValues}
            setValue={setValue}
            client={clientContext}
          />
          {/* ///////////////////////////////////////////////////////////////////////////LIST OF LOCATION TYPES POINTS */}
          {watch("isLegalPerson") === true ? (
            watch("locations")?.map((location: any, index: number) => (
              <>
                <Grid item xs={12} sm={12} md={12}>
                  <SpecialDivider
                    title={`${Vocabulary.workingPoint} / ${Vocabulary.office
                      } (${index + 1})`}
                    button={
                      <Delete onClick={() => deleteWorkingPoint(index)} />
                    }
                  />
                </Grid>
                <Location
                  register={register}
                  control={control}
                  index={index}
                  formState={formState}
                  watch={watch}
                  getValues={getValues}
                  title={Vocabulary.deliveryAddress}
                />
              </>
            ))
          ) : (
            <>
              <Location
                register={register}
                control={control}
                watch={watch}
                index={0}
                formState={formState}
                getValues={getValues}
                title={Vocabulary.deliveryAddress}
              />
            </>
          )}
        </Grid>
        {/* Add Location type button */}
        {watch("isLegalPerson") === true && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 16,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => {
                addWorkingPoint();
              }}
            >
              {Vocabulary.addNewWorkingPoint}
            </Button>
          </div>
        )}
      </form>
    </Fragment>
  );
}
